import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { DEFAULT_LIMIT } from 'src/constants';
import { isAuthorizedQuery } from 'src/selectors/isAuthorizedQuery';
import { useQueryProductVersions } from 'src/hooks/useQueryProductVersions';
import { UseQueryOptions, UseQueryResult } from '@tanstack/react-query';

type ProductVersionsQueryKey = App.ProductSearch.ProductVersionsQueryKey;
type ProductVersionsResponse = MCP.Products.ProductSearch.Models.ProductVersionsResponse;

export type ResultType = Omit<UseQueryResult<ProductVersionsResponse, Error>, 'data'> & {
    productVersionOptions: string[],
    currentProductVersion: string | null,
};

export const useProductVersions = (
    productKey: string | undefined | null,
    options?: UseQueryOptions<ProductVersionsResponse, Error, ProductVersionsResponse, ProductVersionsQueryKey>,
): ResultType => {
    const accessToken = useRecoilValue(isAuthorizedQuery);
    const {
        data: productVersions,
        ...rest
    } = useQueryProductVersions(accessToken, { productKey: productKey as string, limit: DEFAULT_LIMIT }, {
        ...options,
        refetchInterval: false,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: false,
        enabled: !!productKey && !!accessToken,
    });

    const productVersionOptions: string[] = useMemo(() => (
        productVersions?.result
            ? productVersions.result.map(
                (item: MCP.Products.ProductSearch.Models.ProductVersion) => item.version.toString(),
            ) : []
    ), [productVersions]);

    const currentProductVersion = useMemo(() => {
        if (productVersions?.result) {
            const defaultVersion = productVersions.result.find(
                (item: MCP.Products.ProductSearch.Models.ProductVersion) => item.isCurrent,
            );

            return defaultVersion ? defaultVersion.version.toString() : null;
        }
        return null;
    }, [productVersions]);

    return { productVersionOptions, currentProductVersion, ...rest };
};
